/**
 * Lógica pura de acceso LMS (detalle interno de AccessContext).
 * API pública: importar desde `contexts/AccessContext` (`useAccess`, `getAccess`).
 */
import { getUserSession } from 'helpers/getUserSession.helper'
import {
  ICurrentLearningPathScope,
  ICurrentPermissions,
  ICurrentPermissionsFlags,
  PermissionKey,
  PERMISSION_KEYS,
} from 'interfaces/currentPermissions'
import { hasGrantedCourseAccess } from './grantedCourses'

export type SessionAccess = {
  /** Admin JWT / manageLms / superadmin → acceso total LMS */
  hasFullAccess: boolean
  isAdmin: boolean
  isSuperadmin: boolean
  manageLmsPermission: boolean
}

export type LmsAccessInput = {
  hasFullAccess: boolean
  /** Claim JWT superadmin (subconjunto de admin total). */
  isSuperadmin?: boolean
  permissions: ICurrentPermissionsFlags | Record<string, never>
  courses: number[]
  learningPath: ICurrentLearningPathScope[]
}

export type LmsAccess = {
  hasFullAccess: boolean
  /** True si admin JWT / acceso total */
  isAdmin: () => boolean
  /** Claim JWT superadmin (analytics / casos que lo distinguen). */
  isSuperadmin: () => boolean
  /** Alguna de las keys ABAC (admin → siempre true) */
  can: (key: PermissionKey | PermissionKey[]) => boolean
  /** Todas las keys (admin → siempre true) */
  canAll: (keys: PermissionKey[]) => boolean
  /**
   * Acceso al curso por scope ABAC (`courses` + cursos dentro de `learningPath`).
   * Admin → true. Única fuente frontend para alcance por curso.
   */
  canAccessCourse: (courseId: number) => boolean
  /**
   * Acceso al programa por scope ABAC (`learningPath[].id`).
   * Admin → true. Única fuente frontend para alcance por programa.
   */
  canAccessLearningPath: (learningPathId: number) => boolean
  /** Tiene al menos un flag ABAC y no es admin total */
  isScopedUser: () => boolean
  /** Sin flags ni scopes → usuario final */
  isEndUser: () => boolean
  canApproveCourseRequests: () => boolean
  canManageCourses: () => boolean
  /** Gestión admin de cursos externos (`manageExternalCourses`), no la vista colaborador «Mis cursos externos». */
  hasExternalCourseAccess: () => boolean
  /** Descarga de reportes cuando además puede ver ficha o monitoreo */
  canDownloadReportWithViewAccess: () => boolean
  /** Editar curso desde el listado — contenido (`manageInternalCourses`) o solo perfiles (`manageParticipants`) */
  canEditCourseFromList: () => boolean
  /** Vista Gestión de cursos (gestión, perfiles/participantes o ficha; no solo `downloadReport`) */
  canAccessCourseManagementView: () => boolean
  /** Listado `/administration/courses` (internos); alineado con pestaña Cursos del sidebar */
  canAccessInternalCoursesList: () => boolean
  /** Vista previa de curso/lección */
  canPreviewCourse: () => boolean
  /** Ficha de participante (perfil / resultados) */
  canViewParticipantProfile: () => boolean
  /** Ver resultados de un participante en un curso */
  canViewParticipantResults: () => boolean
  /** Sección Monitoreo global (tabs participantes, áreas, cursos…) — solo `viewMonitoring` */
  canAccessMonitoringSection: () => boolean
  /** Ficha de curso (ruta `/administration/monitoring/courses/:id`) */
  canAccessCourseSheet: () => boolean
  /** Importar participantes a cursos masivamente (listado de cursos) — solo `manageParticipants` */
  canImportParticipantsToCoursesMassively: () => boolean
  /** Asignar perfiles obligatorios/opcionales en formulario de curso (`manageParticipants`) */
  canAssignCourseProfiles: () => boolean
  /** Enviar correo masivo a participantes desde ficha de curso */
  canSendCourseBulkEmail: () => boolean
  /** Descargar tareas/archivos subidos por participantes */
  canDownloadParticipantTaskFiles: () => boolean
  /** Snapshot de flags con `all` (preferir `can` / `isAdmin` para validar). */
  flags: { all: boolean } & ICurrentPermissionsFlags
  courses: number[]
  learningPath: ICurrentLearningPathScope[]
}

const readSessionSafe = () => {
  try {
    return getUserSession()
  } catch {
    return {} as ReturnType<typeof getUserSession>
  }
}

/** Lee claims de sesión JWT (Core). Admin ABAC (rol is_default) se resuelve vía GET roles/current → isAdmin. */
export const readSessionAccess = (): SessionAccess => {
  const userSession = readSessionSafe()

  const manageLmsPermission = !!userSession['permissions']?.['manageLms']
  const isAdminClaim = !!(
    userSession.isAdmin ||
    userSession.is_admin ||
    userSession.permission?.admin
  )
  const isSuperadmin = !!(
    (userSession as { isSuperadmin?: boolean }).isSuperadmin ||
    userSession.is_superadmin ||
    userSession.permission?.superadmin
  )

  const hasFullAccess = !!(isAdminClaim || manageLmsPermission || isSuperadmin)

  return {
    hasFullAccess,
    isAdmin: hasFullAccess,
    isSuperadmin,
    manageLmsPermission,
  }
}

const flagValue = (
  permissions: ICurrentPermissionsFlags | Record<string, never>,
  key: PermissionKey
): boolean => !!(permissions as ICurrentPermissionsFlags)[key]

const hasAnyPermissionFlag = (
  permissions: ICurrentPermissionsFlags | Record<string, never>
): boolean => PERMISSION_KEYS.some((key) => flagValue(permissions, key))

const toFlags = (
  hasFullAccess: boolean,
  permissions: ICurrentPermissionsFlags | Record<string, never>
): { all: boolean } & ICurrentPermissionsFlags => {
  if (hasFullAccess) {
    return {
      all: true,
      manageInternalCourses: true,
      manageExternalCourses: true,
      manageLearningPaths: true,
      viewCourseDetails: true,
      downloadReport: true,
      manageParticipants: true,
      manageProgress: true,
      manageCourseRequests: true,
      manageMessages: true,
      viewMonitoring: true,
    }
  }

  return {
    all: false,
    manageInternalCourses: flagValue(permissions, 'manageInternalCourses'),
    manageExternalCourses: flagValue(permissions, 'manageExternalCourses'),
    manageLearningPaths: flagValue(permissions, 'manageLearningPaths'),
    viewCourseDetails: flagValue(permissions, 'viewCourseDetails'),
    downloadReport: flagValue(permissions, 'downloadReport'),
    manageParticipants: flagValue(permissions, 'manageParticipants'),
    manageProgress: flagValue(permissions, 'manageProgress'),
    manageCourseRequests: flagValue(permissions, 'manageCourseRequests'),
    manageMessages: flagValue(permissions, 'manageMessages'),
    viewMonitoring: flagValue(permissions, 'viewMonitoring'),
  }
}

/**
 * Evaluador puro: JWT (hasFullAccess) + payload ABAC.
 * Toda decisión de permiso / scope debe pasar por aquí.
 */
export const createLmsAccess = (input: LmsAccessInput): LmsAccess => {
  const {
    hasFullAccess,
    isSuperadmin = false,
    permissions,
    courses = [],
    learningPath = [],
  } = input

  const flags = toFlags(hasFullAccess, permissions)

  const can = (key: PermissionKey | PermissionKey[]): boolean => {
    if (hasFullAccess) return true
    const keys = Array.isArray(key) ? key : [key]
    return keys.some((k) => flagValue(permissions, k))
  }

  const canAll = (keys: PermissionKey[]): boolean => {
    if (hasFullAccess) return true
    return keys.every((k) => flagValue(permissions, k))
  }

  const canAccessCourse = (courseId: number): boolean => {
    if (hasFullAccess) return true
    const id = Number(courseId)
    if (hasGrantedCourseAccess(id)) return true
    if (courses.some((course) => Number(course) === id)) return true
    return learningPath.some((path) =>
      (path.courses || []).some((course) => Number(course) === id)
    )
  }

  const canAccessLearningPath = (learningPathId: number): boolean => {
    if (hasFullAccess) return true
    const id = Number(learningPathId)
    return learningPath.some((path) => Number(path.id) === id)
  }

  return {
    hasFullAccess,
    isAdmin: () => hasFullAccess,
    isSuperadmin: () => !!isSuperadmin,
    can,
    canAll,
    canAccessCourse,
    canAccessLearningPath,
    isScopedUser: () => !hasFullAccess && hasAnyPermissionFlag(permissions),
    isEndUser: () =>
      !hasFullAccess &&
      !hasAnyPermissionFlag(permissions) &&
      courses.length === 0 &&
      learningPath.length === 0,
    canApproveCourseRequests: () => can('manageCourseRequests'),
    canManageCourses: () =>
      can(['manageInternalCourses', 'manageExternalCourses']),
    hasExternalCourseAccess: () => can('manageExternalCourses'),
    canDownloadReportWithViewAccess: () =>
      can('downloadReport') && can(['viewMonitoring', 'viewCourseDetails']),
    canEditCourseFromList: () =>
      can(['manageInternalCourses', 'manageParticipants']),
    canAccessCourseManagementView: () =>
      can([
        'manageInternalCourses',
        'manageExternalCourses',
        'manageParticipants',
        'viewCourseDetails',
      ]),
    canAccessInternalCoursesList: () =>
      can(['manageInternalCourses', 'viewCourseDetails', 'manageParticipants']),
    canPreviewCourse: () =>
      can(['manageInternalCourses', 'viewCourseDetails', 'viewMonitoring']),
    canViewParticipantProfile: () =>
      can(['manageParticipants', 'manageProgress', 'viewCourseDetails']),
    canViewParticipantResults: () =>
      can(['manageProgress', 'viewCourseDetails', 'viewMonitoring']),
    canAccessMonitoringSection: () => can('viewMonitoring'),
    canAccessCourseSheet: () => can(['viewMonitoring', 'viewCourseDetails']),
    canImportParticipantsToCoursesMassively: () => can('manageParticipants'),
    canAssignCourseProfiles: () => can('manageParticipants'),
    canSendCourseBulkEmail: () => can(['manageProgress', 'viewCourseDetails']),
    canDownloadParticipantTaskFiles: () =>
      can(['manageProgress', 'viewCourseDetails']),
    flags,
    courses,
    learningPath,
  }
}

export const createLmsAccessFromPayload = (
  hasFullAccess: boolean,
  payload: ICurrentPermissions,
  isSuperadmin = false
): LmsAccess =>
  createLmsAccess({
    hasFullAccess,
    isSuperadmin,
    permissions: payload.permissions || {},
    courses: payload.courses || [],
    learningPath: payload.learningPath || [],
  })
